const DEFAULT_THRESHOLD = 0.5
const DEFAULT_DWELL_MS = 1000

// framework-agnostic 曝光偵測器：元素進入可視範圍達到 threshold 比例，
// 且持續停留超過 dwellMs 才視為「真的被看到」，每個元素只觸發一次。
// Vue/Stimulus/純 JS 皆可共用（之後 WordPress blog 也能沿用同一份邏輯）。
class ImpressionObserver {
  constructor({ threshold = DEFAULT_THRESHOLD, dwellMs = DEFAULT_DWELL_MS } = {}) {
    this.threshold = threshold
    this.dwellMs = dwellMs
    this.timers = new WeakMap()
    this.callbacks = new WeakMap()
    this.seen = new WeakSet()
    this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {
      threshold: this.threshold,
    })
  }

  observe(element, onImpression) {
    if (!element || typeof onImpression !== 'function' || this.seen.has(element)) return

    this.callbacks.set(element, onImpression)
    this.observer.observe(element)
  }

  unobserve(element) {
    this.clearTimer(element)
    this.callbacks.delete(element)
    this.observer.unobserve(element)
  }

  disconnect() {
    this.observer.disconnect()
  }

  handleIntersect(entries) {
    entries.forEach((entry) => {
      const element = entry.target
      if (this.seen.has(element)) return

      if (entry.isIntersecting && entry.intersectionRatio >= this.threshold) {
        this.startTimer(element)
      } else {
        this.clearTimer(element)
      }
    })
  }

  startTimer(element) {
    if (this.timers.has(element)) return

    const timerId = setTimeout(() => this.markSeen(element), this.dwellMs)
    this.timers.set(element, timerId)
  }

  clearTimer(element) {
    const timerId = this.timers.get(element)
    if (timerId) {
      clearTimeout(timerId)
      this.timers.delete(element)
    }
  }

  markSeen(element) {
    this.seen.add(element)
    this.clearTimer(element)

    const callback = this.callbacks.get(element)
    this.callbacks.delete(element)
    this.observer.unobserve(element)

    if (callback) callback()
  }
}

let sharedInstance = null

// 大部分模組共用同一顆 observer 即可，避免每個元件各自建立 IntersectionObserver。
// 需要不同 threshold/dwellMs 的情境，直接 `new ImpressionObserver(options)` 建立獨立實例。
export const getSharedImpressionObserver = () => {
  if (!sharedInstance) {
    sharedInstance = new ImpressionObserver()
  }
  return sharedInstance
}

export default ImpressionObserver
